import type { ReferencedAppSettings } from "@certa/studio-core";
import type { AppSettings } from "@certa/queries/types/appSettings.types";
import { uniq, uniqBy, intersectionBy } from "lodash-es";

export const getUniqueSettings = (
  appSettings: ReferencedAppSettings
): Record<keyof ReferencedAppSettings, string[]> => {
  return {
    group: uniq(appSettings.group.map(g => g.name)).sort(),
    processStatus: uniq(appSettings.processStatus.map(ps => ps.tag)).sort(),
    objectStatus: uniq(appSettings.objectStatus.map(os => os.tag)).sort(),
    processKind: uniq(appSettings.processKind.map(pk => pk.tag)).sort(),
    emailTemplate: uniq(appSettings.emailTemplate.map(et => et.slug)).sort(),
    emailAttachment: uniq(
      appSettings.emailAttachment.map(ea => ea.title)
    ).sort(),
    extraJSON: uniq(appSettings.extraJSON.map(ej => ej.tag)).sort(),
    businessUnit: uniq(appSettings.businessUnit.map(bu => bu.code)).sort(),
    region: uniq(appSettings.region.map(r => r.code)).sort(),
    alertCategory: uniq(appSettings.alertCategory.map(ac => ac.tag)).sort(),
    processPDFTemplate: uniq(
      appSettings.processPDFTemplate.map(ppt => ppt.tag)
    ).sort(),
    commentFlagOption: uniq(
      appSettings.commentFlagOption.map(cfo => cfo.tag)
    ).sort(),
    objectTypeWorkflowKindRelation: uniq(
      appSettings.objectTypeWorkflowKindRelation.map(
        otwkr => `${otwkr.objectType}-${otwkr.workflowKind}-${otwkr.relation}`
      )
    ).sort(),
    integrationConfig: uniq(
      appSettings.integrationConfig.map(ic => ic.apiType)
    ).sort(),
    integrationToken: uniq(
      appSettings.integrationToken.map(it => it.tokenType)
    ).sort()
  };
};

export const filterAppSettingsByOriginal = (
  mergedSettings: AppSettings,
  originalAppSettings: AppSettings
): AppSettings => {
  return {
    group: intersectionBy(
      mergedSettings.group,
      originalAppSettings.group,
      "name"
    ),
    processStatus: intersectionBy(
      mergedSettings.processStatus,
      originalAppSettings.processStatus,
      "tag"
    ),
    objectStatus: intersectionBy(
      mergedSettings.objectStatus,
      originalAppSettings.objectStatus,
      "tag"
    ),
    processKind: intersectionBy(
      mergedSettings.processKind,
      originalAppSettings.processKind,
      "tag"
    ),
    emailTemplate: intersectionBy(
      mergedSettings.emailTemplate,
      originalAppSettings.emailTemplate,
      "slug"
    ),
    emailAttachment: intersectionBy(
      mergedSettings.emailAttachment,
      originalAppSettings.emailAttachment,
      "title"
    ),
    extraJSON: intersectionBy(
      mergedSettings.extraJSON,
      originalAppSettings.extraJSON,
      "tag"
    ),
    businessUnit: intersectionBy(
      mergedSettings.businessUnit,
      originalAppSettings.businessUnit,
      "code"
    ),
    region: intersectionBy(
      mergedSettings.region,
      originalAppSettings.region,
      "code"
    ),
    alertCategory: intersectionBy(
      mergedSettings.alertCategory,
      originalAppSettings.alertCategory,
      "tag"
    ),
    processPDFTemplate: intersectionBy(
      mergedSettings.processPDFTemplate,
      originalAppSettings.processPDFTemplate,
      "tag"
    ),
    commentFlagOption: intersectionBy(
      mergedSettings.commentFlagOption,
      originalAppSettings.commentFlagOption,
      "tag"
    ),
    objectTypeWorkflowKindRelation: intersectionBy(
      mergedSettings.objectTypeWorkflowKindRelation,
      originalAppSettings.objectTypeWorkflowKindRelation,
      item => `${item.objectType}-${item.workflowKind}-${item.relation}`
    ),
    integrationConfig: intersectionBy(
      mergedSettings.integrationConfig,
      originalAppSettings.integrationConfig,
      "apiType"
    ),
    integrationToken: intersectionBy(
      mergedSettings.integrationToken,
      originalAppSettings.integrationToken,
      "tokenType"
    )
  };
};

export const mergeAppSettings = (
  existingAppSettings: ReferencedAppSettings,
  newAppSettings: ReferencedAppSettings
): ReferencedAppSettings => {
  return {
    group: uniqBy(
      [...newAppSettings.group, ...existingAppSettings.group],
      "name"
    ),
    processStatus: uniqBy(
      [...newAppSettings.processStatus, ...existingAppSettings.processStatus],
      "tag"
    ),
    objectStatus: uniqBy(
      [...newAppSettings.objectStatus, ...existingAppSettings.objectStatus],
      "tag"
    ),
    processKind: uniqBy(
      [...newAppSettings.processKind, ...existingAppSettings.processKind],
      "tag"
    ),
    emailTemplate: uniqBy(
      [...newAppSettings.emailTemplate, ...existingAppSettings.emailTemplate],
      "slug"
    ),
    emailAttachment: uniqBy(
      [
        ...newAppSettings.emailAttachment,
        ...existingAppSettings.emailAttachment
      ],
      "title"
    ),
    extraJSON: uniqBy(
      [...newAppSettings.extraJSON, ...existingAppSettings.extraJSON],
      "tag"
    ),
    businessUnit: uniqBy(
      [...newAppSettings.businessUnit, ...existingAppSettings.businessUnit],
      "code"
    ),
    region: uniqBy(
      [...newAppSettings.region, ...existingAppSettings.region],
      "code"
    ),
    alertCategory: uniqBy(
      [...newAppSettings.alertCategory, ...existingAppSettings.alertCategory],
      "tag"
    ),
    processPDFTemplate: uniqBy(
      [
        ...newAppSettings.processPDFTemplate,
        ...existingAppSettings.processPDFTemplate
      ],
      "tag"
    ),
    commentFlagOption: uniqBy(
      [
        ...newAppSettings.commentFlagOption,
        ...existingAppSettings.commentFlagOption
      ],
      "tag"
    ),
    objectTypeWorkflowKindRelation: uniqBy(
      [
        ...newAppSettings.objectTypeWorkflowKindRelation,
        ...existingAppSettings.objectTypeWorkflowKindRelation
      ],
      item => `${item.objectType}-${item.workflowKind}-${item.relation}`
    ),
    integrationConfig: uniqBy(
      [
        ...newAppSettings.integrationConfig,
        ...existingAppSettings.integrationConfig
      ],
      "apiType"
    ),
    integrationToken: uniqBy(
      [
        ...newAppSettings.integrationToken,
        ...existingAppSettings.integrationToken
      ],
      "tokenType"
    )
  };
};
